/**
 * @file RouteGuards.tsx
 * @brief Role-based route guard components.
 *
 * @details
 * - `RequireAuth`         — any authenticated user.
 * - `RequireSuperAdmin`   — only `role === 'superadmin'`.
 * - `RequireCompanyAdmin` — only `role === 'admin' | 'company_admin'`.
 * - `RequireEmployeeApp`  — plain `role === 'user'` Boardroom web space
 *   (`/app/*`). Admins are bounced to their control-plane homes.
 * - `RequireBoardroomEntitlement` — blocks when company toggled boardroom off.
 * - `RequireNotesEntitlement` — the same, for Personal Notes.
 *
 * @author DES
 * @date 26-05-2026
 */

import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "./auth-context";
import { homePathForUser, isCompanyAdminRole } from "./homePath";
import { isAuthPending } from "./signInFlow";
import { FullScreenLoader } from "@/components/feedback/FullScreenLoader";
import { env } from "@/shared/config/env";
import { companyIdOf, webInterpretAllowed } from "@/features/interpret/pilot";
import {
  BOOT_LOADER_MIN_MS,
  useMinimumHold,
} from "@/shared/hooks/useMinimumHold";

function useAuthBootstrapping(): boolean {
  const { status } = useAuth();
  return useMinimumHold(isAuthPending(status), BOOT_LOADER_MIN_MS);
}

/** "Reconnecting…" while the server cannot be reached (21-10-2026): the
 *  token is kept and AuthContext retries; never the login page. */
function bootLabel(status: string): string {
  return status === "reconnecting" ? "Reconnecting…" : "Signing you in…";
}

export function RequireAuth({ children }: { children?: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  const loading = useAuthBootstrapping();

  if (loading) return <FullScreenLoader label={bootLabel(status)} immediate={status === "reconnecting"} />;

  if (status !== "authenticated") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children ? <>{children}</> : <Outlet />;
}

export function RequireSuperAdmin({ children }: { children?: ReactNode }) {
  const { status, user } = useAuth();
  const location = useLocation();
  const loading = useAuthBootstrapping();

  if (loading) return <FullScreenLoader label={bootLabel(status)} immediate={status === "reconnecting"} />;

  if (status !== "authenticated") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (user?.role !== "superadmin") {
    return <Navigate to="/forbidden" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}

export function RequireCompanyAdmin({ children }: { children?: ReactNode }) {
  const { status, user } = useAuth();
  const location = useLocation();
  const loading = useAuthBootstrapping();

  if (loading) return <FullScreenLoader label={bootLabel(status)} immediate={status === "reconnecting"} />;

  if (status !== "authenticated") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (user?.role === "superadmin") {
    return <Navigate to="/admin" replace />;
  }

  if (!isCompanyAdminRole(user?.role)) {
    return <Navigate to="/forbidden" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}

/**
 * Employee / individual Boardroom web app (`/app/*`).
 * Superadmins → `/admin`; company admins → `/company`.
 */
export function RequireEmployeeApp({ children }: { children?: ReactNode }) {
  const { status, user } = useAuth();
  const location = useLocation();
  const loading = useAuthBootstrapping();

  if (loading) return <FullScreenLoader label={bootLabel(status)} immediate={status === "reconnecting"} />;

  if (status !== "authenticated") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (user?.role === "superadmin" || isCompanyAdminRole(user?.role)) {
    return <Navigate to={homePathForUser(user!)} replace />;
  }

  if (user?.role !== "user") {
    return <Navigate to="/forbidden" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}

/** Gate a route when the company turned Boardroom off for employees. */
export function RequireBoardroomEntitlement({
  children,
  redirectTo = "/app/meetings",
}: {
  children: ReactNode;
  redirectTo?: string;
}) {
  const { entitlements, status } = useAuth();
  const loading = useAuthBootstrapping();

  if (loading || status !== "authenticated") {
    return <FullScreenLoader label="Getting things ready…" />;
  }

  if (entitlements.boardroom === false) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

/**
 * Gate a route when the company turned Personal Notes off for employees.
 *
 * Same shape as the boardroom guard, on the flag that already rides on `/me`.
 * `redirectTo` has no single sensible default here the way the boardroom one
 * does — Notes is mounted in both the employee shell and the company shell, so
 * the caller passes the home its own shell owns.
 */
export function RequireNotesEntitlement({
  children,
  redirectTo,
}: {
  children: ReactNode;
  redirectTo: string;
}) {
  const { entitlements, status } = useAuth();
  const loading = useAuthBootstrapping();

  if (loading || status !== "authenticated") {
    return <FullScreenLoader label="Getting things ready…" />;
  }

  if (entitlements.notes === false) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

/**
 * Gate the live interpretation screen (01-10-2026): the user's
 * `interpretation` entitlement AND the web pilot list
 * (`features/interpret/pilot.ts`). Mounted in both shells, so the caller passes
 * its own home.
 */
export function RequireWebInterpretation({
  children,
  redirectTo,
}: {
  children: ReactNode;
  redirectTo: string;
}) {
  const { entitlements, status, user } = useAuth();
  const loading = useAuthBootstrapping();

  if (loading || status !== "authenticated") {
    return <FullScreenLoader label="Getting things ready…" />;
  }

  if (
    entitlements.interpretation === false ||
    !webInterpretAllowed(companyIdOf(user), env.webInterpretCompanies)
  ) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  const loading = useAuthBootstrapping();

  if (loading) return <FullScreenLoader label="Getting things ready…" />;

  if (status === "authenticated" && user) {
    return <Navigate to={homePathForUser(user)} replace />;
  }

  return <>{children}</>;
}
